import React, { useState } from "react";
import {
  Box,
  Button,
  VStack,
  Text,
  Container,
  HStack,
  useColorModeValue,
  SimpleGrid,
  Divider,
} from "@chakra-ui/react";
import ProfileCompletePrompt from "../components/Dashboard/ProfileCompletePrompt";
import ActiveUserDashboard from "../components/Dashboard/ActiveUserDashboard";

/**
 * Demo page to test the simplified Profile Completion approach
 * Shows the simple prompt instead of complex celebration flow
 */
export default function ProfileCompletionDemo() {
  const [demoMode, setDemoMode] = useState("prompt"); // prompt | dashboard | both

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");

  const mockApplications = [
    { id: 1, title: "Frontend Developer", company: "TechCorp", analyzed: true, resumeGenerated: true },
    { id: 2, title: "React Developer", company: "StartupXYZ", analyzed: true, resumeGenerated: false },
    { id: 3, title: "Full Stack Engineer", company: "BigTech", analyzed: false, resumeGenerated: false },
  ];

  const mockUserStats = {
    jdsAnalyzed: 12,
    resumesGenerated: 8,
    avgMatchScore: 87,
    totalApplications: 15,
  };

  const handleViewResume = () => {
    console.log("Navigate to master resume");
  };

  const handleDismiss = () => {
    console.log("Prompt dismissed");
  };

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Container maxW="6xl">
        <VStack spacing={8}>
          {/* Demo Controls */}
          <Box bg={cardBg} p={6} borderRadius="xl" shadow="sm" w="full">
            <VStack spacing={4} textAlign="center">
              <Text fontSize="2xl" fontWeight="bold">
                Profile Completion Demo (Simplified)
              </Text>
              
              <Text color="gray.600" maxW="600px">
                Much simpler approach: Just show a prominent prompt on the dashboard when profile is complete.
                No complex animations or flows - just a clear CTA to view the master resume.
              </Text>

              <HStack spacing={4}>
                <Button
                  colorScheme={demoMode === "prompt" ? "orange" : "gray"}
                  onClick={() => setDemoMode("prompt")}
                >
                  Just Prompt
                </Button>
                <Button
                  colorScheme={demoMode === "dashboard" ? "orange" : "gray"}
                  onClick={() => setDemoMode("dashboard")}
                >
                  Dashboard Only
                </Button>
                <Button
                  colorScheme={demoMode === "both" ? "orange" : "gray"}
                  onClick={() => setDemoMode("both")}
                >
                  Both Together
                </Button>
              </HStack>
            </VStack>
          </Box>

          {/* Demo Content */}
          <Box w="full">
            {demoMode === "prompt" && (
              <Box maxW="4xl" mx="auto">
                <Text fontSize="lg" fontWeight="medium" mb={4} textAlign="center">
                  Profile Complete Prompt (Standalone)
                </Text>
                <ProfileCompletePrompt
                  onViewMasterResume={handleViewResume}
                  onDismiss={handleDismiss}
                  basicInfo={{ name: "John Doe" }}
                />
              </Box>
            )}

            {demoMode === "dashboard" && (
              <Box>
                <Text fontSize="lg" fontWeight="medium" mb={4} textAlign="center">
                  Dashboard with Highlighted Master Resume
                </Text>
                <ActiveUserDashboard
                  applications={mockApplications}
                  userStats={mockUserStats}
                  profileCompletionPercentage={100}
                  showProfileCompletePrompt={false}
                />
              </Box>
            )}

            {demoMode === "both" && (
              <Box>
                <Text fontSize="lg" fontWeight="medium" mb={4} textAlign="center">
                  Complete Experience: Prompt + Enhanced Dashboard
                </Text>
                <ActiveUserDashboard
                  applications={mockApplications}
                  userStats={mockUserStats}
                  profileCompletionPercentage={100}
                  showProfileCompletePrompt={true}
                />
              </Box>
            )}
          </Box>

          <Divider />

          {/* Integration Guide */}
          <Box bg={cardBg} p={6} borderRadius="xl" shadow="sm" w="full">
            <VStack spacing={4} align="start">
              <Text fontSize="lg" fontWeight="medium">
                Simple Integration:
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full">
                <VStack spacing={3} align="start">
                  <Text fontSize="md" fontWeight="medium" color="green.600">
                    ✅ What This Approach Does:
                  </Text>
                  <VStack spacing={2} align="start" fontSize="sm" color="gray.600">
                    <Text>• Shows simple prompt when profile hits 100%</Text>
                    <Text>• Highlights master resume in quick actions</Text>
                    <Text>• One-time experience (uses localStorage)</Text>
                    <Text>• Clean, non-intrusive design</Text>
                    <Text>• Direct navigation to master resume</Text>
                  </VStack>
                </VStack>

                <VStack spacing={3} align="start">
                  <Text fontSize="md" fontWeight="medium" color="blue.600">
                    🔧 How to Integrate:
                  </Text>
                  <VStack spacing={2} align="start" fontSize="sm" color="gray.600">
                    <Text>• Add `?profile_completed=true` to URL after completion</Text>
                    <Text>• Pass `profileCompletionPercentage` to dashboard</Text>
                    <Text>• Prompt auto-shows for 100% completion</Text>
                    <Text>• User can dismiss or click to view resume</Text>
                    <Text>• Master resume gets special highlighting</Text>
                  </VStack>
                </VStack>
              </SimpleGrid>

              <Box
                p={4}
                bg="blue.50"
                borderRadius="lg"
                border="1px solid"
                borderColor="blue.200"
                w="full"
              >
                <Text fontSize="sm" color="blue.700" fontWeight="medium" mb={2}>
                  💡 Why This Approach is Better:
                </Text>
                <Text fontSize="sm" color="blue.600" lineHeight="1.5">
                  Research shows that simple, clear CTAs perform better than complex animations. 
                  Users want to quickly understand what they've achieved and what to do next. 
                  This approach celebrates completion while immediately guiding them to the valuable outcome - their master resume.
                </Text>
              </Box>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}